import React, { useCallback, useEffect, useState } from 'react';

import { Button, Icons, Select, SpinnerOverlay } from '@components/common';
import FlatTreeXML from '@components/common/FlatXMLTree';

import useMutateConfigurationTemplateValidationRules from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplateValidationRules';
import useQueryTemplatesValidationValidationRulesFolders from '@hooks/cmModule/configurationTemplates/useQueryTemplatesValidationValidationRulesFolders';

import { useModal } from '@components/common/Modal/utils';
import { getXMLString } from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/utils';

import { IConfigurationTemplate } from '@interfaces/cmModule/configurationTemplates';
import { TDefaultObjectType } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IIsRuleAvailable } from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { TMapTree } from '@components/common/FlatXMLTree/interface';
import { ISubmitTemplateTree } from '../interfaces';

import { FLAT_NODE_PATH_SEPARATOR } from '@constants/common';
import { VALIDATION_FOLDER_AVAILABILITY_ERROR, VALIDATION_FOLDER_EMPTY_ERROR } from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/constants';

import styles from './ConfigurationTemplateTreeModal.module.scss';

interface IConfigurationTemplateTreeModalProps {
	data: TMapTree;
	template?: TDefaultObjectType<IConfigurationTemplate>;
	isLoading?: boolean;
	setIsDirtyForm: (isDirty: boolean) => void;
	closeViewModal: () => void;
	onSubmit: (data: ISubmitTemplateTree) => void;
}

const ConfigurationTemplateTreeModal = ({
	data,
	template,
	isLoading,
	setIsDirtyForm,
	onSubmit,
}: IConfigurationTemplateTreeModalProps) => {
	const { onClose } = useModal();

	const [treeData, setTreeData] = useState<TMapTree>(data);
	const [changedIds, setChangedIds] = useState<string[]>([]);

	const defaultValidation = template?.validationRulesFolderId
		? {
			label: template.validationRulesFolderName,
			value: template.validationRulesFolderId,
			isRulesAvailable: true,
		} : null;

	const [selectedValidationFolder, setSelectedValidationFolder] =
		useState<TSelectOption<string, IIsRuleAvailable>>(defaultValidation);

	const validationRulesFolderId = selectedValidationFolder?.value || '';
	const validationRulesFolderName = selectedValidationFolder?.label?.toString() || '';
	const isValidationFolderChanged = selectedValidationFolder?.value !== defaultValidation?.value;

	const {
		validationRulesFolders,
		isLoadingValidationRulesFolders,
	} = useQueryTemplatesValidationValidationRulesFolders();

	const {
		getValidationRules,
		isLoadingValidationRules,
	} = useMutateConfigurationTemplateValidationRules(validationRulesFolderId);

	const getValidationUpdateString = useCallback(async (id: string): Promise<any | undefined> => {
		if (!id || !validationRulesFolderId) return;

		const node = treeData.get(id);

		const {
			key: name,
			description: nameSpace,
			keysPath: path,
		} = node || {};

		return {
			name,
			nameSpace,
			path,
			validationRulesFolderId,
		};
	}, [
		validationRulesFolderId,
		treeData,
	]);

	const validationErrors = new Map();

	const selectError = !selectedValidationFolder
		? VALIDATION_FOLDER_EMPTY_ERROR
		: !selectedValidationFolder?.isRulesAvailable
			? VALIDATION_FOLDER_AVAILABILITY_ERROR
			: undefined;

	const handleSubmit = useCallback(() => {
		if (!validationRulesFolderName || !validationRulesFolderId) {
			return;
		}

		onSubmit({
			tree: treeData,
			folderId: validationRulesFolderId,
			folderName: validationRulesFolderName,
		});
	}, [
		validationRulesFolderName,
		validationRulesFolderId,
		treeData,
		onSubmit,
	]);

	const getCopiedNode = useCallback((id: string) => {
		const node = treeData.get(id);

		if (!node) {
			return '';
		}

		const nodesToCopy = node.path.split(FLAT_NODE_PATH_SEPARATOR).map(item => treeData.get(item));
		const treeToCopy = new Map(nodesToCopy.map(node => [node!.id, node!]));

		return getXMLString(treeToCopy);

	}, [treeData]);

	const handleReset = useCallback(() => {
		setTreeData(data);
		setChangedIds([]);
	}, [data]);

	useEffect(() => {
		setIsDirtyForm(!!changedIds.length);
	}, [changedIds, setIsDirtyForm]);

	return (
		<div className={styles.wrapper}>
			{(isLoading || isLoadingValidationRules) && <SpinnerOverlay />}
			<div className={styles.control}>
				<Select
					errorMessage={selectError}
					handleChange={setSelectedValidationFolder}
					isLoading={isLoadingValidationRulesFolders}
					isMulti={false}
					isWithoutUseForms
					label='Set YANG Model Collection'
					name='model'
					options={validationRulesFolders}
					placeholder='Choose YANG model collection'
					required
					value={selectedValidationFolder}
				/>
			</div>
			<div className={styles.treeWrapper}>
				<FlatTreeXML
					data={treeData}
					errors={undefined}
					getCopiedNode={getCopiedNode}
					getValidationRules={getValidationRules}
					getValidationUpdateString={getValidationUpdateString}
					isReadOnly={!validationRulesFolderId}
					isWithAdd
					isWithCopy
					isWithSkip
					setChangedNodeIds={setChangedIds}
					setData={setTreeData}
				/>
			</div>
			<div className={styles.footer}>
				<div className={styles.errorsCount}>
					{validationErrors?.size > 0 && (
						<span>Errors: {validationErrors?.size}</span>
					)}
				</div>
				<div className={styles.buttonsWrapper}>
					<div className={styles.control}>
						<Button
							disabled={!changedIds.length}
							icon={Icons.refresh()}
							large
							onClick={handleReset}
							text='Reset'
							withoutBackground
						/>
					</div>
					<div className={styles.control}>
						<Button
							large
							onClick={onClose}
							text='Close'
						/>
						<Button
							disabled={!!selectError || (!changedIds.length && !isValidationFolderChanged)}
							large
							onClick={handleSubmit}
							text='Save template'
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ConfigurationTemplateTreeModal;
